import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../context/AuthContext";
import {
  Activity,
  TrendingUp,
  UserCheck,
  Users,
  BarChart2,
  Loader2,
} from "lucide-react";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function parseReservaDate(r) {
  if (!r) return null;
  try {
    const f = r.fecha;
    if (f?.toDate) return f.toDate();
    if (f instanceof Date) return f;
    if (typeof f === "number") return new Date(f);
    if (typeof f === "string" && /^\d{4}-\d{2}-\d{2}/.test(f)) {
      const [Y, M, D] = f.split("-").map(Number);
      return new Date(Y, M - 1, D);
    }
    return new Date(f);
  } catch {
    return null;
  }
}

function isCompleted(r) {
  const s = (r.estadoSesion || r.estado || "").toLowerCase();
  return ["completada", "realizada", "hecha", "completado"].includes(s);
}

function isCancelled(r) {
  const s = (r.estadoSesion || r.estado || "").toLowerCase();
  return (
    s.includes("cancel") ||
    s.includes("rechaz") ||
    s.includes("ausente") ||
    s.includes("no asist")
  );
}

function fmtPercent(x) {
  if (x == null || Number.isNaN(x)) return "—";
  return `${Math.round(x)}%`;
}

export default function TrabajadorDesempeno() {
  const { user } = useAuth();
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🔹 Cargar reservas del profesional
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, "reservas"),
      where("profesionalId", "==", user.uid)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setReservas(items);
        setLoading(false);
      },
      () => setLoading(false)
    );

    return () => unsub();
  }, [user?.uid]);

  // 🔹 Calcular stats
  const stats = useMemo(() => {
    if (!reservas.length) return null;

    const now = new Date();
    const start30 = new Date(now.getTime() - 30 * MS_PER_DAY);

    const last30 = [];
    const sesionesPorPaciente = new Map();

    reservas.forEach((r) => {
      const d = parseReservaDate(r);
      if (!d) return;

      if (d >= start30 && d <= now) last30.push({ ...r, _date: d });

      const uid = r.uid;
      if (uid) {
        const arr = sesionesPorPaciente.get(uid) || [];
        arr.push({ ...r, _date: d });
        sesionesPorPaciente.set(uid, arr);
      }
    });

    let completadas30 = 0;
    let canceladas30 = 0;
    let pendientes30 = 0;

    last30.forEach((r) => {
      if (isCompleted(r)) completadas30++;
      else if (isCancelled(r)) canceladas30++;
      else pendientes30++;
    });

    const total30 = last30.length;

    const tasaAsistencia =
      completadas30 + canceladas30 > 0
        ? (completadas30 / (completadas30 + canceladas30)) * 100
        : null;

    let pacientesNuevos = 0;
    let pacientesRecurrentes = 0;

    sesionesPorPaciente.forEach((arr) => {
      let primera = null;
      arr.forEach((r) => {
        if (!primera || r._date < primera) primera = r._date;
      });

      if (primera >= start30) pacientesNuevos++;
      else pacientesRecurrentes++;
    });

    // Servicios top últimos 30 días
    const serviciosMap = new Map();
    last30.forEach((r) => {
      const key =
        r.servicio_raw ||
        r.servicio_categoria ||
        r.servicio_variante ||
        "Otro servicio";
      serviciosMap.set(key, (serviciosMap.get(key) || 0) + 1);
    });

    const servicios = Array.from(serviciosMap.entries())
      .map(([nombre, cantidad]) => ({ nombre, cantidad }))
      .sort((a, b) => b.cantidad - a.cantidad)
      .slice(0, 4);

    return {
      total30,
      completadas30,
      canceladas30,
      pendientes30,
      tasaAsistencia,
      pacientesNuevos,
      pacientesRecurrentes,
      servicios,
    };
  }, [reservas]);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-semibold">Mi desempeño clínico</h1>
        <p className="text-xs md:text-sm text-gray-600 mt-1">
          Resumen de tus sesiones y pacientes en los últimos 30 días.
        </p>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Cargando datos…
        </div>
      )}

      {!loading && !reservas.length && (
        <div className="text-sm text-gray-600 bg-white border rounded-xl px-3 py-2">
          Aún no tienes sesiones registradas como profesional.
        </div>
      )}

      {!loading && reservas.length > 0 && stats && (
        <>
          {/* 🔵 Tarjetas principales (responsivas) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Sesiones */}
            <CardStat
              title="Sesiones últimos 30 días"
              value={stats.total30 || 0}
              icon={<Activity className="w-4 h-4 text-blue-700" />}
              subtitle={`${stats.completadas30} completadas · ${stats.canceladas30} canceladas`}
            />

            {/* Asistencia */}
            <CardStat
              title="Tasa de asistencia"
              value={fmtPercent(stats.tasaAsistencia)}
              icon={<TrendingUp className="w-4 h-4 text-emerald-700" />}
              subtitle="Completadas vs canceladas"
            />

            {/* Nuevos */}
            <CardStat
              title="Pacientes nuevos"
              value={stats.pacientesNuevos}
              icon={<UserCheck className="w-4 h-4 text-indigo-700" />}
              subtitle="Primera sesión en los últimos 30 días"
            />

            {/* Recurrentes */}
            <CardStat
              title="Pacientes recurrentes"
              value={stats.pacientesRecurrentes}
              icon={<Users className="w-4 h-4 text-purple-700" />}
              subtitle="Ya estaban antes del período"
            />
          </div>

          {/* 🔵 Servicios más frecuentes */}
          <div className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
            <div className="flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-blue-700" />
              <h2 className="text-sm font-semibold">
                Servicios más frecuentes (últimos 30 días)
              </h2>
            </div>

            {!stats.servicios.length && (
              <p className="text-xs text-gray-500">
                No hay suficientes datos para mostrar aún.
              </p>
            )}

            {!!stats.servicios.length && (
              <div className="space-y-3">
                {stats.servicios.map((s) => {
                  const pct =
                    stats.total30 > 0
                      ? Math.round((s.cantidad / stats.total30) * 100)
                      : 0;

                  return (
                    <div key={s.nombre} className="space-y-1">
                      <div className="flex justify-between text-xs md:text-sm text-gray-700">
                        <span>{s.nombre}</span>
                        <span>
                          {s.cantidad} sesión{s.cantidad !== 1 ? "es" : ""} ·{" "}
                          {pct}%
                        </span>
                      </div>

                      <div className="w-full h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className="h-full bg-blue-600 rounded-full"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* 🔵 Subcomponente tarjeta */
function CardStat({ title, value, icon, subtitle }) {
  return (
    <div className="bg-white rounded-xl border shadow-sm p-3 flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">{title}</span>
        {icon}
      </div>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs text-gray-500">{subtitle}</div>
    </div>
  );
}
